import {Avalanche, RecodeLogic} from '@fantasmagames/slot/src/logic/integration/RecodeLogic';
import {Symbols} from 'src/reels/Symbols';
import {GameReelConstants} from 'src/reels/GameReelConstants';
import {ReelConstants, SymbolPosition} from '@fantasmagames/slot/src/modules/reels/ReelConstants';
import {GameRecodeLogic} from 'src/logic/recode/GameRecodeLogic';

export class RecodeToPrototypeConverter {

	public static serverSymbolNames: string[] = [];

	public static convertInit(data: any, logic: RecodeLogic): any {
		const response: any = {};

		response.limits = {};
		response.limits.defaultBet = logic.chipSize;
		response.limits.legalBets = [];
		for (const chip of logic.validChips) {
			response.limits.legalBets.push(chip * logic.chipSize);
		}

		response.stats = {};
		response.stats.b = logic.currentBalance;

		return {...response, ...this.convertSpin(data, logic)};
	}

	public static convertSpin(data: any, logic: RecodeLogic): any {
		const response: any = {};

		response.stats = {};
		response.stats.b = logic.currentBalance;

		const win: number = Number.parseFloat(data.Response.Slot.attributes.win);
		const freeSpinsLeft: number = Number.parseInt(data.Response.Slot.attributes.freeSpins, 10);
		const freeSpinsTotalAwarded: number = Number.parseInt(data.Response.Slot.attributes.freeSpinTotalAwarded, 10);

		response.totalFS = freeSpinsTotalAwarded;
		response.spinWin = win;
		response.spinsLeft = freeSpinsLeft;

		const roundTypes: number[] = logic.roundTypes;
		let roundIndex: number = 0;

		const initialWildMeterValue: number = logic.isFreeSpinsStarted ? logic.wildMeter - logic.getTotalWildMeterDeltas() : 0;
		let currentWildMeter: number = initialWildMeterValue;

		response.generations = [];
		for (const reels of logic.reels) {
			const currentSymbols: string[][] = this.makeProtSymbolsFromRecode(reels);
			const generation: any = {
				runningTotal: 0,
				wildMeter: 0,
				winnings: [],
				win: 0,
				symbols: currentSymbols,
				symbolsNew: [],
				removal: [],
			};
			response.generations.push(generation);

			const currentRoundType: number = roundTypes[roundIndex + 1];
			if (currentRoundType === 2) {
				generation.removal = this.makeRemovalsList(data, logic, roundIndex, currentSymbols);
			}

			currentWildMeter += logic.wildMeterDeltas[roundIndex] !== undefined ? logic.wildMeterDeltas[roundIndex] : 0;
			generation.wildMeter = currentWildMeter;

			roundIndex++;
		}

		if (data.Response.Slot.Wins) {
			this.parseWins(data.Response.Slot.Wins, response.generations);
		}

		response.spinWin = win;

		if (freeSpinsLeft === 0) {
			const reels = (logic as GameRecodeLogic).freeSpinsTriggerReels;
			const symbols = this.makeProtSymbolsFromRecode(reels);
			(logic as GameRecodeLogic).updateFreeSpinsTriggerSymbols(symbols);
		}

		return response;
	}

	protected static makeRemovalsList(data: any, logic: RecodeLogic, roundIndex: number, reels: string[][]): any {
		const removal: any = [];
		const avalanche: Avalanche = logic.avalanches[roundIndex];
		const respins: SymbolPosition[] = this.findAllRespins(reels);
		const itemsPerRespin: number = Math.floor(avalanche.symbolIndexes.length / respins.length);

		let currentItemInRespin: number = 0;
		let respinIndex: number = 0;

		let currentRemovalObject: any = {removal: [], x: respins[respinIndex].x, y: respins[respinIndex].y};
		for (const symRecPos of avalanche.symbolIndexes) {
			const symbolPos = {
				x: symRecPos % ReelConstants.REELS_AMOUNT,
				y: Math.trunc(symRecPos / ReelConstants.REELS_AMOUNT),
			};

			let isCorrectRemoval: boolean = true;

			for (const respin of respins) {
				if ((symbolPos.x === respin.x && symbolPos.y === respin.y)) {
					isCorrectRemoval = false;
				}
			}

			if (isCorrectRemoval) {
				currentRemovalObject.removal.push({x: symbolPos.x, y: symbolPos.y});
			}

			currentItemInRespin++;
			if (currentItemInRespin >= itemsPerRespin && respinIndex !== respins.length - 1) {
				respinIndex++;
				currentItemInRespin = 0;

				removal.push(currentRemovalObject);
				currentRemovalObject = {removal: [], x: respins[respinIndex].x, y: respins[respinIndex].y};
			}
		}

		removal.push(currentRemovalObject);

		return removal;
	}

	protected static findAllRespins(reels: string[][]): SymbolPosition[] {
		const skullsPositions: SymbolPosition[] = [];
		for (let reelIndex: number = 0; reelIndex < reels.length; reelIndex++) {
			for (let rowIndex: number = 0; rowIndex < reels[reelIndex].length; rowIndex++) {
				if (reels[reelIndex][rowIndex] === 'RS' && rowIndex >= 3) {
					skullsPositions.push({x: reelIndex, y: rowIndex});
				}
			}
		}

		return skullsPositions;
	}

	protected static parseWins(data: any, generations: any[]): void {
		if (!data.Win.length) {
			data.Win = [data.Win];
		}
		let currentWinIndex: number = 0;
		let currentRunningTotal: number = 0;
		for (const win of data.Win) {
			const winReason: string = win.attributes.reason;
			// ordinary wins
			const winValue: any = {
				amount: 0,
				multiplier: win.attributes.multiplier ? Number.parseInt(win.attributes.multiplier, 10) : 1,
				payableID: Number.parseInt(win.attributes.paytableId, 10),
				payline: Number.parseInt(win.attributes.payline, 10),
				reason: win.attributes.reason,
				round: Number.parseInt(win.attributes.round, 10),
				ways: Number.parseInt(win.attributes.ways, 10),
				winSymbols: [],
			};
			winValue.amount = (Number.parseInt(win.attributes.amount, 10) * RecodeLogic.CENT_TO_A_DOLLAR) / winValue.multiplier;
			win.attributes.winSymbols.split(',').forEach((value) => {
				winValue.winSymbols.push(Number.parseInt(value, 10));
			});

			const isRtoL: number = winValue.reason[winValue.reason.length - 1] === 'R' ? 1 : 0;
			currentRunningTotal += winValue.amount;
			generations[winValue.round + 1].runningTotal = currentRunningTotal;
			generations[winValue.round].winnings.push(['normal', winValue.payline, winValue.amount, [], 'NONE', isRtoL]);
			const lastWinningIndex: number = generations[winValue.round].winnings.length - 1;
			for (const wp of winValue.winSymbols) {
				const symbolPos = {
					x: wp % ReelConstants.REELS_AMOUNT,
					y: Math.trunc(wp / ReelConstants.REELS_AMOUNT),
				};

				generations[winValue.round].winnings[lastWinningIndex][3].push(symbolPos);
			}

			currentWinIndex++;
		}

		if (generations.length > 1 && generations[generations.length - 1].runningTotal === 0) {
			generations[generations.length - 1].runningTotal = generations[generations.length - 2].runningTotal;
		}
	}

	public static makeProtSymbolsFromRecode(symbols: number[]): string[][] {
		const reels: string[][] = [];

		let symbolIndex: number = 0;
		let reelIndex: number = 0;

		for (let symIndex = 0; symIndex < symbols.length; symIndex++) {
			const symbol: number = symbols[symIndex];

			// for (const symbol of symbols) {
			if (!reels[reelIndex]) {
				reels.push([]);
			}

			const gameSymbol: string = RecodeToPrototypeConverter.recodeToPrototypeSymbol(symbol);
			if (gameSymbol) {
				reels[reelIndex].push(gameSymbol);
			}
			reelIndex++;
			if (reelIndex > GameReelConstants.REELS_AMOUNT - 1) {
				symbolIndex++;
				reelIndex = 0;
			}
		}

		return reels;
	}

	public static recodeToGameSymbol(recodeSymbolID: number): Symbols {
		switch (recodeSymbolID) {
			case 1:
				return Symbols.WR;
			case 2:
				return Symbols.M1;
			case 3:
				return Symbols.M2;
			case 4:
				return Symbols.M3;
			case 5:
				return Symbols.M4;
			case 6:
				return Symbols.M5;
			case 7:
				return Symbols.F6;
			case 8:
				return Symbols.F7;
			case 9:
				return Symbols.F8;
			case 10:
				return Symbols.F9;
			case 11:
				return Symbols.F10;
			case 12:
				return Symbols.BN;
			case 13:
				return Symbols.RS;

			default:
				return -1;
		}
	}

	public static recodeToPrototypeSymbol(recodeSymbolID: number): string {
		switch (recodeSymbolID) {
			case 1:
				return 'WR';
			case 2:
				return 'M1';
			case 3:
				return 'M2';
			case 4:
				return 'M3';
			case 5:
				return 'M4';
			case 6:
				return 'M5';
			case 7:
				return 'F6';
			case 8:
				return 'F7';
			case 9:
				return 'F8';
			case 10:
				return 'F9';
			case 11:
				return 'F10';
			case 12:
				return 'BN';
			case 13:
				return 'RS';

			default:
				return ' ';
		}
	}

	public static gameSymbolNameToId(symbolName: string): Symbols {
		switch (symbolName) {
			case 'WR':
				return 0;
			case 'M1':
				return 1;
			case 'M2':
				return 2;
			case 'M3':
				return 3;
			case 'M4':
				return 4;
			case 'M5':
				return 5;
			case 'F6':
				return 6;
			case 'F7':
				return 7;
			case 'F8':
				return 8;
			case 'F9':
				return 9;
			case 'F10':
				return 10;
			case 'BN':
				return 11;
			case 'RS':
				return 12;

			default:
				return -1;
		}
	}
}



// WEBPACK FOOTER //
// ./src/logic/recode/RecodeToPrototypeConverter.ts